/**
 * Shopping Preferences Validation Utilities
 *
 * Pure functions for validating preference data structures.
 */

import {
  ShoppingPreferences,
  ShoppingPrefsPayload,
  ValidationResult,
  WOMEN_UNIVERSE,
  MEN_UNIVERSE,
} from '@lib/shopping-preferences/types';

/**
 * Valid universe values
 */
const VALID_UNIVERSES = [WOMEN_UNIVERSE, MEN_UNIVERSE];

/**
 * Validate universe value
 */
export const isValidUniverse = (universe: unknown): universe is number => {
  return typeof universe === 'number' && VALID_UNIVERSES.includes(universe);
};

/**
 * Validate categories array
 */
export const isValidCategories = (categories: unknown): categories is number[] => {
  if (!Array.isArray(categories)) {
    return false;
  }

  return categories.every((cat) => typeof cat === 'number' && cat > 0);
};

/**
 * Validate custom array
 */
export const isValidCustom = (custom: unknown): custom is string[] => {
  if (!Array.isArray(custom)) {
    return false;
  }

  return custom.every((item) => typeof item === 'string' && item.length > 0);
};

/**
 * Validate ISO date string
 */
export const isValidISODate = (date: unknown): boolean => {
  if (typeof date !== 'string') {
    return false;
  }

  const parsed = new Date(date);
  return !isNaN(parsed.getTime()) && parsed.toISOString() === date;
};

/**
 * Validate shopping preferences payload
 */
export const validatePreferencesPayload = (payload: unknown): ValidationResult => {
  const errors: string[] = [];

  if (!payload || typeof payload !== 'object') {
    return { isValid: false, errors: ['Payload must be an object'] };
  }

  const prefs = payload as Partial<ShoppingPrefsPayload>;

  // Validate universe (optional)
  if (prefs.universe !== undefined && !isValidUniverse(prefs.universe)) {
    errors.push(`Invalid universe: ${prefs.universe}. Must be ${WOMEN_UNIVERSE} or ${MEN_UNIVERSE}`);
  }

  // Validate categories (optional)
  if (prefs.categories !== undefined) {
    if (!isValidCategories(prefs.categories)) {
      errors.push('Invalid categories: must be an array of positive numbers');
    }
  }

  // Validate custom (optional)
  if (prefs.custom !== undefined) {
    if (!isValidCustom(prefs.custom)) {
      errors.push('Invalid custom: must be an array of non-empty strings');
    }
  }

  // Validate updated_at (optional)
  if (prefs.updated_at !== undefined && !isValidISODate(prefs.updated_at)) {
    errors.push('Invalid updated_at: must be an ISO date string');
  }

  return {
    isValid: errors.length === 0,
    errors: errors.length > 0 ? errors : undefined,
  };
};

/**
 * Validate full shopping preferences (with last_updated)
 */
export const validatePreferences = (preferences: unknown): ValidationResult => {
  const payloadResult = validatePreferencesPayload(preferences);

  if (!payloadResult.isValid) {
    return payloadResult;
  }

  const prefs = preferences as Partial<ShoppingPreferences>;

  // last_updated is required for full preferences
  if (!prefs.last_updated) {
    return { isValid: false, errors: ['last_updated is required'] };
  }

  if (!isValidISODate(prefs.last_updated)) {
    return { isValid: false, errors: ['Invalid last_updated: must be an ISO date string'] };
  }

  return { isValid: true };
};

/**
 * Sanitize preferences by removing invalid fields
 */
export const sanitizePreferences = (preferences: Partial<ShoppingPrefsPayload>): ShoppingPrefsPayload => {
  const sanitized: ShoppingPrefsPayload = {};

  if (isValidUniverse(preferences.universe)) {
    sanitized.universe = preferences.universe;
  }

  if (preferences.categories && isValidCategories(preferences.categories)) {
    sanitized.categories = preferences.categories;
  }

  if (preferences.custom && isValidCustom(preferences.custom)) {
    sanitized.custom = preferences.custom;
  }

  if (preferences.updated_at && isValidISODate(preferences.updated_at)) {
    sanitized.updated_at = preferences.updated_at;
  }

  return sanitized;
};

/**
 * Check if preferences have been selected by user (not defaults)
 */
export const hasUserSelectedPreferences = (preferences: ShoppingPreferences | null): boolean => {
  if (!preferences) {
    return false;
  }

  // User has selected if any of these are explicitly set:
  // - Universe is explicitly set (either 1 or 2)
  // - Categories array has items
  // - Custom array has items
  return !!(
    (preferences.universe !== undefined && preferences.universe !== null) ||
    (preferences.categories && preferences.categories.length > 0) ||
    (preferences.custom && preferences.custom.length > 0)
  );
};
